import { Link } from 'react-router-dom';
import { buttonVariants } from '@/components/ui/Button';
import {
  ArrowRight,
  Clapperboard,
  Globe,
  Lock,
  ShoppingBag,
  Sparkles,
  Users,
} from 'lucide-react';

const FEATURES = [
  {
    icon: Globe,
    title: 'Japanese & English at launch',
    body: 'Every original ships with both Japanese and English audio on day one — switch any time, no sub-before-dub waiting.',
  },
  {
    icon: Lock,
    title: 'Studio-owned originals',
    body: "Everything on GoBingeBox is Nullify Entertainment's own work — produced, cast, and credited in-house.",
  },
  {
    icon: Sparkles,
    title: 'Three simple tiers',
    body: 'Pick monthly or annual, add simultaneous streams when you need them, and change or cancel any time.',
  },
  {
    icon: ShoppingBag,
    title: 'Merch for everyone',
    body: 'The store is open to all — and subscribers get a discount that grows with their tier.',
  },
] as const;

export function HomePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-radial">
        <div className="container-page flex flex-col items-center py-20 text-center md:py-28">
          <span className="eyebrow mb-5 animate-fade-in">A Nullify Entertainment studio</span>
          <h1 className="max-w-4xl animate-fade-in font-display text-display-sm font-extrabold leading-tight text-ink md:text-display-lg">
            Stream Nullify originals,
            <br className="hidden sm:block" /> <span className="text-gradient">your way.</span>
          </h1>
          <p className="mt-6 max-w-xl animate-fade-in text-lg text-ink-muted">
            Binge our growing catalog in Japanese or English audio. Watch previews free — subscribe
            to unlock every full episode.
          </p>
          <div className="mt-9 flex animate-fade-in flex-wrap items-center justify-center gap-3">
            <Link to="/plans" className={buttonVariants({ variant: 'primary', size: 'lg' })}>
              See plans
              <ArrowRight size={18} />
            </Link>
            <Link to="/browse" className={buttonVariants({ variant: 'secondary', size: 'lg' })}>
              Browse the catalog
            </Link>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="container-page py-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card p-6">
              <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl bg-surface-raised text-brand-soft">
                <Icon size={22} strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-base font-bold text-ink">{title}</h3>
              <p className="mt-2 text-sm text-ink-muted">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About the studio */}
      <section className="border-t border-border bg-panel">
        <div className="container-page grid gap-10 py-16 md:grid-cols-2 md:items-center">
          <div>
            <p className="eyebrow mb-3">About the studio</p>
            <h2 className="font-display text-3xl font-bold text-ink">
              Built by an indie studio, for fans of the craft.
            </h2>
            <p className="mt-4 text-ink-muted">
              Nullify Entertainment is a small studio: a solo founder and a crew of volunteer voice
              actors building original series we'd want to watch ourselves. BingeBox is where those
              shows live.
            </p>
            <p className="mt-4 text-ink-muted">
              Want to help shape what comes next? The Dev Forums are where you can pitch a show or
              request upload access.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/dev" className={buttonVariants({ variant: 'outline', size: 'md' })}>
                <Users size={18} />
                Visit the Dev Forums
              </Link>
              <Link to="/store" className={buttonVariants({ variant: 'ghost', size: 'md' })}>
                <ShoppingBag size={18} />
                Shop merch
              </Link>
            </div>
          </div>
          <div className="card relative grid aspect-video place-items-center overflow-hidden bg-brand-radial">
            <Clapperboard size={56} strokeWidth={1.25} className="text-brand-soft/70" />
            <span className="absolute bottom-4 left-5 text-2xs uppercase tracking-[0.18em] text-ink-faint">
              Now in production
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
